const { ActivePlayers } = require('boardgame.io/core');
const { values, sortBy } = require('lodash');

const DEFAULT_CONFIG = {
  startingScore: 20,
  penalty: 2,
  questionValue: 5,
};

function parsePositiveInt(value, fallback) {
  const n = Number(value);
  if (Number.isFinite(n) && n >= 1) {
    return Math.floor(n);
  }
  return fallback;
}

function parseNonNegInt(value, fallback) {
  const n = Number(value);
  if (Number.isFinite(n) && n >= 0) {
    return Math.floor(n);
  }
  return fallback;
}

function ensureScore(G, id) {
  if (G.hostId && String(id) === String(G.hostId)) {
    return;
  }
  if (G.scores[id] === undefined) {
    G.scores[id] = G.config.startingScore;
  }
}

function sortedQueue(G) {
  return sortBy(values(G.queue), ['timestamp']);
}

function queueHeadId(G) {
  const q = sortedQueue(G);
  return q.length ? q[0].id : null;
}

function resetBuzzers(G) {
  G.queue = {};
}

function resetBuzzer(G, ctx, id) {
  const newQueue = { ...G.queue };
  delete newQueue[id];
  G.queue = newQueue;
}

function toggleLock(G) {
  G.locked = !G.locked;
}

function buzz(G, ctx, id) {
  if (G.locked || G.phase !== 'playing' || G.question.status !== 'open') {
    return;
  }
  if (G.hostId && String(id) === String(G.hostId)) {
    return;
  }
  const pid = String(id);
  if (G.queue[pid]) {
    return;
  }
  ensureScore(G, pid);
  // Server clock — barcha jamoalar uchun bir xil mezon
  G.queue = {
    ...G.queue,
    [pid]: { id: pid, timestamp: Date.now() },
  };
}

/** Persist room settings from host create-form into shared game state */
function setRoomConfig(G, ctx, startingScore, penalty) {
  if (G.phase !== 'lobby') {
    return;
  }
  G.config = {
    startingScore: parseNonNegInt(startingScore, DEFAULT_CONFIG.startingScore),
    penalty: parsePositiveInt(penalty, DEFAULT_CONFIG.penalty),
  };
  G.hostId = String(ctx.playerID);
}

function startGame(G, ctx, questionValue, startingScore, penalty) {
  if (G.phase !== 'lobby') {
    return;
  }

  let priceRaw = questionValue;
  let scoreRaw = startingScore;
  let penRaw = penalty;

  if (questionValue && typeof questionValue === 'object') {
    scoreRaw = questionValue.startingScore;
    penRaw = questionValue.penalty;
    priceRaw =
      questionValue.questionValue != null
        ? questionValue.questionValue
        : questionValue.value;
  }

  const price = parsePositiveInt(priceRaw, DEFAULT_CONFIG.questionValue);
  const score = parseNonNegInt(
    scoreRaw != null ? scoreRaw : G.config.startingScore,
    DEFAULT_CONFIG.startingScore
  );
  const pen = parsePositiveInt(
    penRaw != null ? penRaw : G.config.penalty,
    DEFAULT_CONFIG.penalty
  );

  G.hostId = String(ctx.playerID);
  G.config = { startingScore: score, penalty: pen };
  G.scores = {};
  // Only seed scores for seats that already exist as keys we'll touch via ensureScore.
  // Named players get score on first buzz / host actions; display falls back to startingScore.
  for (let i = 0; i < ctx.numPlayers; i += 1) {
    const id = String(i);
    if (id !== G.hostId) {
      G.scores[id] = score;
    }
  }
  G.queue = {};
  G.locked = false;
  G.phase = 'playing';
  G.question = {
    number: 1,
    value: price,
    status: 'open',
  };
  G.lastEvent = {
    type: 'start',
    questionValue: price,
    startingScore: score,
    at: Date.now(),
  };
}

function nextQuestion(G, ctx, questionValue) {
  if (G.phase !== 'playing') {
    return;
  }
  let raw = questionValue;
  if (questionValue && typeof questionValue === 'object') {
    raw =
      questionValue.questionValue != null
        ? questionValue.questionValue
        : questionValue.value;
  }
  const price = parsePositiveInt(raw, 0);
  if (!price) {
    return;
  }
  G.queue = {};
  G.locked = false;
  G.question = {
    number: (G.question.number || 0) + 1,
    value: price,
    status: 'open',
  };
  G.lastEvent = {
    type: 'nextQuestion',
    questionNumber: G.question.number,
    questionValue: price,
    at: Date.now(),
  };
}

function markIncorrect(G, ctx, playerId) {
  if (G.phase !== 'playing' || G.question.status !== 'open') {
    return;
  }
  const head = queueHeadId(G);
  if (!head || head !== playerId) {
    return;
  }

  ensureScore(G, playerId);
  const penalty = G.config.penalty;
  G.scores[playerId] -= penalty;
  G.question.value += penalty;

  const newQueue = { ...G.queue };
  delete newQueue[playerId];
  G.queue = newQueue;

  G.lastEvent = {
    type: 'incorrect',
    playerId,
    delta: -penalty,
    questionValue: G.question.value,
    at: Date.now(),
  };
}

function markCorrect(G, ctx, playerId) {
  if (G.phase !== 'playing' || G.question.status !== 'open') {
    return;
  }
  const head = queueHeadId(G);
  if (!head || head !== playerId) {
    return;
  }

  ensureScore(G, playerId);
  const prize = G.question.value;
  G.scores[playerId] += prize;
  G.question.status = 'resolved';
  G.queue = {};
  G.locked = true;

  G.lastEvent = {
    type: 'correct',
    playerId,
    delta: prize,
    questionValue: prize,
    at: Date.now(),
  };
}

function adjustScore(G, ctx, playerId, delta) {
  ensureScore(G, playerId);
  G.scores[playerId] += Number(delta) || 0;
  G.lastEvent = {
    type: 'adjust',
    playerId,
    delta: Number(delta) || 0,
    at: Date.now(),
  };
}

function endGame(G) {
  G.phase = 'finished';
  G.locked = true;
  G.queue = {};
  G.question = {
    ...G.question,
    status: 'resolved',
  };
  G.lastEvent = {
    type: 'end',
    at: Date.now(),
  };
}

/**
 * Shu xonada qayta start — jamoalar/nomlar saqlanadi.
 * playing yoki finished dan → lobby (sozlama + o‘yinni boshlash).
 */
function restartMatch(G, ctx, startingScore, penalty) {
  if (G.phase !== 'playing' && G.phase !== 'finished') {
    return;
  }
  if (G.hostId && String(ctx.playerID) !== String(G.hostId)) {
    return;
  }

  const score = parseNonNegInt(
    startingScore != null ? startingScore : G.config.startingScore,
    DEFAULT_CONFIG.startingScore
  );
  const pen = parsePositiveInt(
    penalty != null ? penalty : G.config.penalty,
    DEFAULT_CONFIG.penalty
  );

  G.hostId = String(ctx.playerID);
  G.config = { startingScore: score, penalty: pen };
  G.scores = {};
  G.queue = {};
  G.locked = false;
  G.phase = 'lobby';
  G.question = { number: 0, value: 0, status: 'idle' };
  G.lastEvent = {
    type: 'restart',
    startingScore: score,
    penalty: pen,
    at: Date.now(),
  };
}

const Buzzer = {
  name: 'buzzer',
  minPlayers: 2,
  maxPlayers: 64,
  setup: () => ({
    queue: {},
    locked: false,
    hostId: null,
    config: {
      startingScore: DEFAULT_CONFIG.startingScore,
      penalty: DEFAULT_CONFIG.penalty,
    },
    scores: {},
    question: { number: 0, value: 0, status: 'idle' },
    phase: 'lobby',
    lastEvent: null,
  }),
  phases: {
    play: {
      start: true,
      moves: {
        buzz,
        resetBuzzer,
        resetBuzzers,
        toggleLock,
        setRoomConfig,
        startGame,
        nextQuestion,
        markIncorrect,
        markCorrect,
        adjustScore,
        endGame,
        restartMatch,
      },
      turn: {
        activePlayers: ActivePlayers.ALL,
      },
    },
  },
};

module.exports = { Buzzer, DEFAULT_CONFIG };
